import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Calendar, ShieldCheck, MapPin, Award, ThumbsUp, ArrowRight, Car, Mail, Facebook, Clock, User, Star, Check } from "lucide-react";
import * as Icons from "lucide-react";
import { companyInfo } from "../data";
import { fetchCollection, fetchPageBySlug } from "../lib/cms";
import ReviewsSlider from "../components/ReviewsSlider";

// Dynamisk ikon-komponent
function ServiceIcon({ name, className }) {
  const IconComponent = Icons[name] || Icons.Wrench;
  return <IconComponent className={className} />;
}

export default function Home() {
  const [pageData, setPageData] = useState(null);
  const [services, setServices] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);

  const googleReviews = [
    {
      initials: "TH",
      name: "Thomas Hansen",
      location: "Skærbæk",
      text: "Super professionel betjening og lynhurtig dækskifte. Kan varmt anbefales!"
    },
    {
      initials: "MN",
      name: "Mette Nielsen",
      location: "Tønder",
      text: "Altid ærlig service og gennemsigtige priser. Her føler man sig tryg som kunde."
    },
    {
      initials: "JL",
      name: "Jesper Lassen",
      location: "Skærbæk",
      text: "Fantastisk service hver gang. De finder altid fejlen hurtigt og til en god pris."
    }
  ];

  useEffect(() => {
    async function loadData() {
      try {
        const [servicesData, galleriesData] = await Promise.all([
          fetchCollection("services"),
          fetchCollection("galleries")
        ]);
        setServices(servicesData);
        setGalleries(galleriesData);
      } catch (error) {
        console.error("Fejl ved indlæsning af forside-data:", error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    document.title = "Brdr. Lorenzen ApS | Serviceværksted Skærbæk";
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Vi ved præcis, hvordan netop din bil skal håndteres. Book tid til din opgave allerede nu - klik her.');

    let ogImage = document.querySelector('meta[property="og:image"]');
    if (!ogImage) {
      ogImage = document.createElement('meta');
      ogImage.setAttribute('property', 'og:image');
      document.head.appendChild(ogImage);
    }
    ogImage.setAttribute('content', '');
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentReviewIndex((prev) => (prev + 1) % googleReviews.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const getServiceImage = (gallerySlug) => {
    const gallery = galleries.find(g => g.slug_id === gallerySlug);
    return gallery && gallery.images && gallery.images.length > 0
      ? gallery.images[0]
      : "https://assets.auxodev.dk/assets/845935e5-3993-406d-896b-e0a4423b6917/cf74c1bb-4b40-4160-820d-1fa8ea095f1d.jpg";
  };

  return (
    <div className="bg-slate-900 text-white antialiased relative">
      
      {/* 1. FLOATING CONTACT SIDEBAR (Inspireret af auto-marine.dk) */}
      <div className="fixed right-0 top-1/2 -translate-y-1/2 z-40 flex flex-col space-y-2">
        <a
          href={`tel:${companyInfo.phone}`}
          className="flex items-center justify-center h-12 w-12 rounded-l-lg bg-blue-600 hover:bg-blue-700 text-white shadow-2xl border-l border-y border-blue-400/20 transition-all duration-200 hover:-translate-x-1 group"
          title="Ring til os"
        >
          <Phone className="h-5 w-5" />
        </a>
        <Link
          to="/online-booking"
          className="flex items-center justify-center h-12 w-12 rounded-l-lg bg-blue-600 hover:bg-blue-700 text-white shadow-2xl border-l border-y border-blue-400/20 transition-all duration-200 hover:-translate-x-1 group"
          title="Book tid online"
        >
          <Calendar className="h-5 w-5" />
        </Link>
        <Link
          to="/kontakt"
          className="flex items-center justify-center h-12 w-12 rounded-l-lg bg-blue-600 hover:bg-blue-700 text-white shadow-2xl border-l border-y border-blue-400/20 transition-all duration-200 hover:-translate-x-1 group"
          title="Vores adresse & kontakt"
        >
          <User className="h-5 w-5" />
        </Link>
      </div>

      {/* 2. HERO SECTION (Inspireret af auto-marine.dk) */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden py-24 md:py-32">
        {/* Baggrundsbillede med mørkt overlay */}
        <div className="absolute inset-0 z-0 bg-slate-950">
          <video
            src="https://assets.auxodev.dk/assets/845935e5-3993-406d-896b-e0a4423b6917/5294dd01-6167-435e-a934-f67d6491e7a5.mov"
            autoPlay
            loop
            muted
            playsInline
            poster="https://assets.auxodev.dk/assets/845935e5-3993-406d-896b-e0a4423b6917/cf74c1bb-4b40-4160-820d-1fa8ea095f1d.jpg"
            className="w-full h-full object-cover object-center scale-105 filter brightness-75 contrast-110"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/50 to-slate-950/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 via-transparent to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl text-left space-y-8">
            {/* Eyebrow */}
            <div className="inline-flex items-center gap-2 border-l-2 border-blue-600 bg-blue-500/5 px-4 py-1.5 text-xs font-bold tracking-widest text-white uppercase">
              Din lokale mekaniker i Skærbæk siden 1976
            </div>

            {/* Main Title */}
            <h1 className="text-3xl font-extrabold tracking-tight sm:text-5xl md:text-6xl text-white leading-tight uppercase">
              AUTOVÆRKSTED <br />
              <span className="text-blue-500">TIL ALLE BEHOV</span>
            </h1>

            {/* Description */}
            <p className="max-w-2xl text-base md:text-lg text-slate-300 leading-relaxed">
              Hos BRDR. LORENZEN. SKÆRBÆK ApS tager vi os godt af din bil. Vi leverer professionel reparation og service af alle bilmærker med over 40 års erfaring – og du beholder altid fabriksgarantien.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Link
                to="/online-booking"
                className="rounded-none bg-blue-600 px-8 py-4 text-sm font-bold uppercase tracking-wider text-white hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/10 active:scale-95"
              >
                Book Tid Online
              </Link>
              <a
                href={`tel:${companyInfo.phone}`}
                className="rounded-none border border-white/20 bg-white/5 backdrop-blur-sm px-8 py-4 text-sm font-bold uppercase tracking-wider text-white hover:bg-white hover:text-slate-950 transition-all active:scale-95"
              >
                Ring: <span className="font-mono">{companyInfo.phoneFormatted}</span>
              </a>
            </div>

            {/* Trustpilot / Google Reviews Block */}
            <div className="pt-8 border-t border-white/10 space-y-4">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-blue-500 text-blue-500" />
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <img
                    src="https://assets.auxodev.dk/assets/845935e5-3993-406d-896b-e0a4423b6917/a3bd7065-4100-4f37-99f4-bf295d2c7e78.svg"
                    alt="Google Logo"
                    className="h-5 w-auto"
                  />
                  <span className="text-xs font-bold tracking-wider text-slate-300 uppercase">
                    Fremragende 4.8 / 5 stjerner baseret på Google anmeldelser
                  </span>
                </div>
              </div>

              {/* Google Reviews Slider */}
              <div className="relative overflow-hidden h-[130px] md:h-[110px] w-full max-w-xl">
                {googleReviews.map((review, idx) => (
                  <div
                    key={idx}
                    className={`absolute inset-0 bg-slate-900/60 backdrop-blur-sm border border-white/10 p-4 transition-all duration-500 flex flex-col justify-between ${
                      idx === currentReviewIndex
                        ? "opacity-100 translate-x-0 pointer-events-auto"
                        : "opacity-0 translate-x-8 pointer-events-none"
                    }`}
                  >
                    <div className="absolute top-4 right-4">
                      <img
                        src="https://assets.auxodev.dk/assets/845935e5-3993-406d-896b-e0a4423b6917/a3bd7065-4100-4f37-99f4-bf295d2c7e78.svg"
                        alt="Google"
                        className="h-3.5 w-auto opacity-40"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-7 w-7 rounded-full bg-blue-500/10 flex items-center justify-center text-xs font-bold text-blue-500">
                        {review.initials}
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-white">{review.name}</h4>
                        <p className="text-[10px] text-slate-400">{review.location} · Google Review</p>
                      </div>
                    </div>
                    <div className="flex gap-0.5 my-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-3 w-3 fill-blue-500 text-blue-500" />
                      ))}
                    </div>
                    <p className="text-xs text-slate-300 italic leading-relaxed line-clamp-2">
                      "{review.text}"
                    </p>
                  </div>
                ))}
              </div>

              {/* Slider Dots */}
              <div className="flex gap-1.5 pt-1">
                {googleReviews.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentReviewIndex(idx)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      idx === currentReviewIndex ? "w-6 bg-blue-600" : "w-1.5 bg-white/20 hover:bg-white/40"
                    }`}
                    aria-label={`Gå til anmeldelse ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. COMPANY INTRODUCTION SECTION (Split Content - Hvid baggrund) */}
      <section className="py-24 bg-white text-slate-900 relative">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            
            {/* Left Column: Text-heavy narrative layout */}
            <div className="lg:col-span-7 space-y-8 text-left">
              <div className="space-y-3">
                <span className="text-xs font-bold uppercase tracking-widest text-blue-600 border-b-2 border-blue-600 pb-1">
                  Velkommen til Brdr. Lorenzen
                </span>
                <h2 className="text-2xl font-extrabold tracking-tight text-slate-950 sm:text-4xl uppercase leading-tight pt-2">
                  DIN SIKKERHED FOR ET <br />
                  <span className="text-blue-600">ORDENTLIGT STYKKE ARBEJDE</span>
                </h2>
              </div>

              <div className="text-slate-600 leading-relaxed space-y-6 text-base">
                <p>
                  Hos os bliver der altid taget godt hånd om dig og din bil. Vi har drevet autoværksted i Skærbæk siden 1976 med fokus på det gode, gedigne håndværk og den personlige relation. Vi er ikke tilknyttet nogen kæde, hvilket sikrer dig lavere priser uden fordyrende mellemled.
                </p>
                <p>
                  Vi følger altid bilfabrikantens forskrifter, så du beholder fabriksgarantien, og vi anvender udelukkende reservedele af original kvalitet. Uanset om du skal have foretaget et serviceeftersyn, klargøring til syn, fejlsøgning eller udbedring af skader på pladeværkstedet, står vores erfarne mekanikere klar til at hjælpe dig.
                </p>
              </div>

              {/* CTAs */}
              <div className="flex flex-wrap items-center gap-4 pt-4">
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="rounded-none bg-blue-600 px-6 py-4 text-xs font-bold uppercase tracking-wider text-white hover:bg-blue-700 transition-all shadow-md"
                >
                  Ring os op: {companyInfo.phoneFormatted}
                </a>
                <Link
                  to="/online-booking"
                  className="rounded-none border border-slate-300 bg-transparent px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-800 hover:bg-slate-50 transition-all"
                >
                  Bestil tid online
                </Link>
              </div>
            </div>

            {/* Right Column: Image Collage (Inspireret af auto-marine.dk asymmetrisk grid) */}
            <div className="lg:col-span-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <img
                  src="https://assets.auxodev.dk/assets/845935e5-3993-406d-896b-e0a4423b6917/27e2baee-5b87-4eef-991b-90b19ff763d8.jpg"
                  alt="Mekaniker i arbejde"
                  className="w-full h-64 object-cover rounded-none grayscale hover:grayscale-0 transition-all duration-300"
                />
                <img
                  src="https://assets.auxodev.dk/assets/845935e5-3993-406d-896b-e0a4423b6917/b2e95a1c-f552-4eb9-844f-126aa2943043.jpg"
                  alt="Værkstedsmiljø"
                  className="w-full h-64 object-cover rounded-none grayscale hover:grayscale-0 transition-all duration-300"
                />
              </div>
              <div>
                <img
                  src="https://assets.auxodev.dk/assets/845935e5-3993-406d-896b-e0a4423b6917/af687075-6fd1-4b61-bfe0-a2a5ed7bf4a6.jpg"
                  alt="Dæk og fælge"
                  className="w-full h-80 object-cover rounded-none grayscale hover:grayscale-0 transition-all duration-300"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 4. CORE SERVICES FEATURE GRID (Mørk baggrund med dæmpet værkstedsbillede) */}
      <section className="relative py-24 overflow-hidden">
        {/* Baggrundsbillede med mørk screen wash */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/845935e5-3993-406d-896b-e0a4423b6917/e094ab39-e40b-4e87-9f20-5bb3a11c12cc.jpg"
            alt="Værksted baggrund"
            className="w-full h-full object-cover object-center filter brightness-15 contrast-125"
          />
          <div className="absolute inset-0 bg-slate-900/90" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-blue-600 border-b-2 border-blue-600 pb-1">
              Hvad kan vi hjælpe med?
            </span>
            <h2 className="text-2xl font-extrabold tracking-tight text-white sm:text-4xl uppercase pt-2">
              PROFESSIONELLE VÆRKSTEDSYDELSER
            </h2>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {loading ? (
              [...Array(6)].map((_, i) => (
                <div key={i} className="h-72 bg-slate-900/40 border border-slate-800 animate-pulse" />
              ))
            ) : (
              services.slice(0, 6).map((service) => (
                <Link
                  key={service.id}
                  to={`/vaerkstedet/${service.slug_id}`}
                  className="relative overflow-hidden flex flex-col justify-between border border-white/10 bg-slate-900/40 p-8 md:p-10 hover:border-blue-500/50 hover:bg-slate-800/30 transition-all duration-300 group"
                >
                  {/* Hover Image Background */}
                  <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
                    <img
                      src={getServiceImage(service.gallery_slug)}
                      alt={service.title}
                      className="w-full h-full object-cover object-center scale-105 group-hover:scale-100 opacity-0 group-hover:opacity-20 transition-all duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>

                  <div className="space-y-6 relative z-10">
                    {/* Outlined white stroke icon */}
                    <div className="text-white group-hover:text-white transition-colors">
                      <ServiceIcon name={service.icon} className="h-10 w-10 stroke-1" />
                    </div>

                    <h3 className="text-lg sm:text-xl font-bold text-white uppercase tracking-wide">
                      {service.title}
                    </h3>

                    <p className="text-sm text-slate-400 leading-relaxed">
                      {service.short_description}
                    </p>
                  </div>

                  <div className="pt-8 relative z-10">
                    <span
                      className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white border-b border-white/20 pb-1 group-hover:text-white group-hover:border-white transition-all"
                    >
                      Læs mere
                      <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </Link>
              ))
            )}
          </div>

          <div className="mt-16 text-center">
            <Link
              to="/service-og-reparation"
              className="inline-flex items-center gap-2 rounded-none bg-blue-600 px-8 py-4 text-xs font-bold uppercase tracking-wider text-white hover:bg-blue-700 transition-all shadow-lg"
            >
              Se alle vores ydelser
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* 5. "FORDELSKLUB" PROMOTION (Vibrant mørkeblå accent-sektion) */}
      <section className="py-20 bg-slate-50 text-slate-900 border-y border-slate-200 relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Left Column: Stamp badge / visual card */}
            <div className="lg:col-span-5 flex justify-center lg:justify-start">
              <div className="relative bg-slate-900 text-white p-8 w-80 h-48 border border-white/10 shadow-2xl flex flex-col justify-between">
                <div className="absolute top-0 right-0 w-16 h-16 bg-white text-slate-950 flex items-center justify-center font-extrabold text-xs uppercase tracking-widest rotate-12 translate-x-4 -translate-y-4 shadow-lg">
                  Gratis
                </div>
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-white">Klubkort</span>
                  <h3 className="text-lg font-extrabold uppercase tracking-wide mt-1">BRDR. LORENZEN</h3>
                  <p className="text-xs text-slate-400 mt-0.5">KUNDEKLUB</p>
                </div>
                <div className="flex justify-between items-end">
                  <div className="text-[10px] font-mono text-slate-500">
                    MEDLEM SIDEN 2026
                  </div>
                  <div className="flex items-center gap-1 text-white">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Text and CTA */}
            <div className="lg:col-span-7 space-y-6 text-left">
              <h2 className="text-2xl font-extrabold tracking-tight sm:text-4xl text-slate-950 uppercase leading-tight">
                BRDR. LORENZEN KUNDEKLUB <br />
                GIVER DIG MASSER AF FORDELE
              </h2>
              <p className="text-slate-600 text-base leading-relaxed max-w-2xl">
                Tilmeld dig vores kundeklub og få eksklusive fordele, automatiske påmindelser om bilsyn, dækskifte og særlige tilbud direkte i din indbakke. Det er helt gratis og uforpligtende at være medlem.
              </p>
              <div className="pt-2">
                <Link
                  to="/online-booking"
                  className="inline-flex items-center gap-2 rounded-none bg-slate-900 text-white px-8 py-4 text-xs font-bold uppercase tracking-wider hover:bg-slate-800 transition-all shadow-lg"
                >
                  Tilmeld dig kundeklubben
                  <ArrowRight className="h-4 w-4 text-blue-500" />
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 6. SOCIAL MEDIA & SMARTPHONE MOCKUP (Dæmpet garage-baggrund) */}
      <section className="relative py-24 overflow-hidden">
        {/* Baggrundsbillede med mørk screen wash */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/845935e5-3993-406d-896b-e0a4423b6917/fab13f61-83de-491e-82c6-ef50b4ad367f.jpg"
            alt="Garage"
            className="w-full h-full object-cover object-center filter brightness-40 contrast-100"
          />
          <div className="absolute inset-0 bg-slate-900/70" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            
            {/* Left Column: Text */}
            <div className="space-y-6 text-left">
              <span className="text-xs font-bold uppercase tracking-widest text-white border-b-2 border-white pb-1">
                Følg os på sociale medier
              </span>
              <h2 className="text-2xl font-extrabold tracking-tight text-white sm:text-4xl uppercase pt-2">
                FØLG VORES AUTOVÆRKSTED <br />
                PÅ SOCIALE MEDIER
              </h2>
              <p className="text-slate-400 text-base leading-relaxed">
                Vi deler løbende hverdagsbilleder fra værkstedet, gode råd til bilpleje og nyheder om vores services på vores Facebook-side. Følg med og bliv en del af vores lokale fællesskab i Skærbæk!
              </p>
              <div className="pt-4">
                <a
                  href="https://www.facebook.com/profile.php?id=100057279183350"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 text-sm font-bold uppercase tracking-wider text-white hover:text-slate-300 transition-colors"
                >
                  <Facebook className="h-5 w-5" />
                  Følg os på Facebook
                </a>
              </div>
            </div>

            {/* Right Column: Smartphone Mockup (CSS-bygget) */}
            <div className="flex justify-center">
              <div className="relative w-72 h-[500px] bg-slate-900 border-[8px] border-slate-800 rounded-[36px] shadow-2xl overflow-hidden flex flex-col">
                {/* Speaker/Camera Notch */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-4 bg-slate-800 rounded-full z-20 flex items-center justify-center">
                  <div className="w-2 h-2 bg-slate-900 rounded-full" />
                </div>

                {/* Screen Content */}
                <div className="flex-1 flex flex-col bg-slate-950 text-white pt-8 text-left text-xs">
                  {/* Mock Facebook Header */}
                  <div className="bg-slate-900 p-3 border-b border-slate-800 flex items-center gap-2">
                  <div className="h-6 w-6 rounded-full bg-blue-600 flex items-center justify-center text-[10px] font-bold text-slate-900">
                      BL
                    </div>
                    <div>
                      <p className="font-bold text-[10px]">BRDR. LORENZEN</p>
                      <p className="text-[8px] text-slate-500">10.245 følgere</p>
                    </div>
                  </div>

                  {/* Mock Post */}
                  <div className="flex-1 overflow-hidden">
                    <iframe
                      src="https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2Fblbiler.dk&tabs=timeline&width=272&height=420&small_header=true&adapt_container_width=true&hide_cover=true&show_facepile=false"
                      width="100%"
                      height="100%"
                      style={{ border: "none", overflow: "hidden" }}
                      scrolling="no"
                      frameBorder="0"
                      allowFullScreen={true}
                      allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                      title="Facebook Feed"
                    />
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>


    </div>
  );
}